// לשונית "ממשקים" במסך יצירה (11 §1, 09): מצב auto/consent לכל ממשק עם side-effect.
// נראית רק ליוצר היצירה ול-admin: member — לא נשאל בכלל; creator — השרת מחליט (403 = לא היוצר → הלשונית לא מוצגת).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@aibify/core';
import { aiApi } from '../lib/aiApi.ts';
import type { ArtifactInterface } from '../lib/aiContract.ts';
import { ApiError } from '../lib/api.ts';
import { T } from '../lib/strings.ts';
import { ErrorView, Loading } from './Status.tsx';
import { Select } from './ui/input.tsx';
import { MailConnectCard } from './MailConnectDrawer.tsx';

export function useInterfacesTab(slug: string, role: Role | undefined) {
  const eligible = role === 'admin' || role === 'creator';
  const q = useQuery({
    queryKey: ['artifact-interfaces', slug],
    queryFn: () => aiApi.artifactInterfaces(slug),
    enabled: eligible,
    retry: false,
  });
  const forbidden = q.error instanceof ApiError && (q.error.status === 403 || q.error.status === 404);
  return { visible: eligible && !forbidden && !q.isPending, query: q };
}

function Row({ slug, it }: { slug: string; it: ArtifactInterface }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState(it.mode);
  const [state, setState] = useState<{ busy?: boolean; ok?: boolean; err?: string }>({});
  const change = async (m: 'auto' | 'consent') => {
    const prev = mode;
    setMode(m);
    setState({ busy: true });
    try {
      await aiApi.setArtifactInterface(slug, it.interfaceId, m);
      setState({ ok: true });
      qc.setQueryData(['artifact-interfaces', slug], (d: { interfaces: ArtifactInterface[] } | undefined) =>
        d ? { interfaces: d.interfaces.map((x) => (x.interfaceId === it.interfaceId ? { ...x, mode: m } : x)) } : d,
      );
      // ממשק scoped: מצב ההיקף (approved/missing) מחושב בשרת — רענון
      if (it.scope) void qc.invalidateQueries({ queryKey: ['artifact-interfaces', slug] });
    } catch (e) {
      setMode(prev);
      setState({ err: e instanceof ApiError ? e.textHe : T.network });
    }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card p-3" data-testid="iface-row" data-iface={it.interfaceId} data-mode={mode}>
      <div className="me-auto flex flex-col">
        <span className="font-medium">{it.nameHe}</span>
        <bdi dir="ltr" className="text-xs text-muted-foreground">
          {it.interfaceId}
        </bdi>
      </div>
      {it.scope && it.sideEffect && it.scope.recipients ? (
        // 20: mail.send — הנמענים שבקוד ומצבם; "שלח בלי לשאול" = היקף ליצירה + מצב auto (מה שמאפשר שליחה מתוזמנת)
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="iface-scope" data-scope-status={it.scope.status}>
          {it.scope.recipients.length ? (
            <span data-testid="iface-recipients">
              {T.iface.scopeRecipients}: <bdi dir="ltr">{it.scope.recipients.join(', ')}</bdi> · {T.iface.scopeStatus[it.scope.status]}
            </span>
          ) : (
            <span className="text-muted-foreground">{T.iface.scopeNoRecipients}</span>
          )}
          <Select aria-label={it.nameHe} className="w-auto" value={mode} disabled={state.busy} onChange={(e) => void change(e.target.value as 'auto' | 'consent')} data-testid="iface-mode">
            <option value="consent">{T.iface.mailConsent}</option>
            <option value="auto">{T.iface.mailAuto}</option>
          </Select>
        </div>
      ) : it.scope ? (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="iface-scope" data-scope-status={it.scope.status}>
          {it.scope.hosts.length ? (
            <span>
              {T.iface.scopeHosts}: <bdi dir="ltr">{it.scope.hosts.join(', ')}</bdi> · {T.iface.scopeStatus[it.scope.status]}
            </span>
          ) : (
            <span className="text-muted-foreground">{T.iface.scopeNone}</span>
          )}
          {it.scope.hosts.length > 0 && it.scope.status !== 'approved' && (
            <button type="button" className="rounded-sm border border-border px-2 py-1" disabled={state.busy} onClick={() => void change('auto')} data-testid="iface-approve-scope">
              {T.iface.approveScope}
            </button>
          )}
          {it.scope.status === 'approved' && (
            <button type="button" className="rounded-sm border border-border px-2 py-1" disabled={state.busy} onClick={() => void change('consent')} data-testid="iface-revoke-scope">
              {T.iface.revokeScope}
            </button>
          )}
        </div>
      ) : it.sideEffect ? (
        <Select
          aria-label={it.nameHe}
          className="w-auto"
          value={mode}
          disabled={state.busy}
          onChange={(e) => void change(e.target.value as 'auto' | 'consent')}
          data-testid="iface-mode"
        >
          <option value="consent">{T.iface.consent}</option>
          <option value="auto">{T.iface.auto}</option>
        </Select>
      ) : (
        <span className="text-sm text-muted-foreground">{T.iface.readOnly}</span>
      )}
      {/* 20: המייל יוצא מה-Gmail של מי שמריץ — כאן רואים אם שלך מחובר, ו"חבר Gmail" פותח את ה-drawer */}
      {it.interfaceId === 'mail.send' && <MailConnectCard caps={['send']} className="w-full" />}
      {state.ok && (
        <span role="status" className="text-sm text-ok" data-testid="iface-saved">
          {T.iface.saved}
        </span>
      )}
      {state.err && (
        <span role="alert" className="w-full text-sm text-error">
          {state.err}
        </span>
      )}
    </li>
  );
}

export function InterfacesTab({ slug, query }: { slug: string; query: ReturnType<typeof useInterfacesTab>['query'] }) {
  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorView error={query.error} onRetry={() => query.refetch()} />;
  return (
    <div className="flex flex-col gap-3" data-testid="interfaces-tab">
      <p className="m-0 text-sm text-muted-foreground">{T.iface.hint}</p>
      {query.data.interfaces.length === 0 && (
        <p className="m-0 text-sm" data-testid="iface-none">
          {T.iface.none}
        </p>
      )}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {query.data.interfaces.map((it) => (
          <Row key={it.interfaceId} slug={slug} it={it} />
        ))}
      </ul>
    </div>
  );
}
